(() => {
  if (customElements.get('er-review-carousel')) return;

  class ErReviewCarousel extends HTMLElement {
    connectedCallback() {
      if (this._connected) return;
      this._track = this.querySelector('[data-reviews-track]');
      this._previous = this.querySelector('[data-review-prev]');
      this._next = this.querySelector('[data-review-next]');
      this._status = this.querySelector('[data-review-status]');
      this._controls = this.querySelector('[data-reviews-controls]');
      if (!this._track || !this._previous || !this._next) return;

      this._connected = true;
      this._window = this.ownerDocument.defaultView || window;
      this._motion = this._window.matchMedia?.('(prefers-reduced-motion: reduce)');
      this._listeners = [];
      this._cards = [];
      this._positions = [];
      this._frame = null;
      this._requestedLeft = null;
      this._pointer = null;
      this._suppressClick = false;
      this._listen(this._previous, 'click', () => this._move(-1));
      this._listen(this._next, 'click', () => this._move(1));
      this._listen(this._track, 'scroll', () => this._queueUpdate(), { passive: true });
      this._listen(this._track, 'scrollend', () => {
        this._requestedLeft = null;
        this._queueUpdate();
      });
      this._listen(this._track, 'wheel', () => {
        this._requestedLeft = null;
        this._queueUpdate();
      }, { passive: true });
      this._listen(this._track, 'keydown', (event) => this._onKeyDown(event));
      this._listen(this._track, 'pointerdown', (event) => this._onPointerDown(event));
      this._listen(this._track, 'pointermove', (event) => this._onPointerMove(event), { passive: false });
      this._listen(this.ownerDocument, 'pointerup', (event) => this._onPointerEnd(event));
      this._listen(this.ownerDocument, 'pointercancel', (event) => this._onPointerEnd(event, true));
      this._listen(this._track, 'lostpointercapture', (event) => this._onPointerEnd(event, true));
      this._listen(this._track, 'click', (event) => {
        if (this._suppressClick && event.detail > 0) {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
        this._suppressClick = false;
      }, true);
      this._listen(this, 'shopify:block:select', (event) => this._onBlockSelect(event));

      if (this._window.ResizeObserver) {
        this._observer = new this._window.ResizeObserver(() => {
          this._requestedLeft = null;
          this._queueUpdate();
        });
        this._observer.observe(this._track);
      } else {
        this._listen(this._window, 'resize', () => {
          this._requestedLeft = null;
          this._queueUpdate();
        });
      }
      this._update();
      if (this._controls) this._controls.hidden = false;
      this.dataset.initialized = 'true';
    }

    disconnectedCallback() {
      if (!this._connected) return;
      this._connected = false;
      this._listeners.forEach(([target, type, listener, options]) => target.removeEventListener(type, listener, options));
      this._listeners = [];
      this._observer?.disconnect();
      this._observer = null;
      if (this._frame !== null) this._window.cancelAnimationFrame(this._frame);
      this._frame = null;
      this._finishPointer(true);
      this._requestedLeft = null;
      this._previous.disabled = true;
      this._next.disabled = true;
      if (this._controls) this._controls.hidden = true;
      delete this.dataset.initialized;
    }

    _listen(target, type, listener, options) {
      target.addEventListener(type, listener, options);
      this._listeners.push([target, type, listener, options]);
    }

    _queueUpdate() {
      if (!this._connected || this._frame !== null) return;
      this._frame = this._window.requestAnimationFrame(() => {
        this._frame = null;
        if (this._connected) this._update();
      });
    }

    _measure() {
      const cards = Array.from(this._track.querySelectorAll('[data-review-card]'));
      if (cards.length !== this._cards.length || cards.some((card, index) => card !== this._cards[index])) {
        this._cards.forEach((card) => this._observer?.unobserve(card));
        this._cards = cards;
        this._cards.forEach((card) => this._observer?.observe(card));
      }
      const rawLeft = Number.isFinite(this._track.scrollLeft) ? this._track.scrollLeft : 0;
      const origin = this._track.getBoundingClientRect().left + (this._track.clientLeft || 0);
      this._positions = this._cards.map((card) => {
        const rectangle = card.getBoundingClientRect();
        const left = Math.max(0, rectangle.left - origin + rawLeft);
        return { left, right: left + rectangle.width };
      });
      this._width = Math.max(0, this._track.clientWidth);
      this._maximum = Math.max(0, this._track.scrollWidth - this._width);
      this._left = this._clamp(rawLeft);
    }

    _clamp(value) {
      return Math.min(this._maximum, Math.max(0, value));
    }

    _update() {
      this._measure();
      if (this._requestedLeft !== null && Math.abs(this._left - this._requestedLeft) <= 1) this._requestedLeft = null;
      const boundary = this._requestedLeft ?? this._left;
      const noOverflow = this._maximum <= 1 || this._cards.length <= 1 || this._width <= 0;
      this._previous.disabled = noOverflow || boundary <= 1;
      this._next.disabled = noOverflow || boundary >= this._maximum - 1;
      if (!this._status) return;
      let start = 0;
      let end = 0;
      if (this._width > 0) {
        this._positions.forEach((position, index) => {
          if (position.right > this._left + 1 && position.left < this._left + this._width - 1) {
            if (start === 0) start = index + 1;
            end = index + 1;
          }
        });
      }
      const template = this.dataset.statusTemplate || '__START__–__END__ / __TOTAL__';
      const status = template.replaceAll('__START__', String(start)).replaceAll('__END__', String(end)).replaceAll('__TOTAL__', String(this._cards.length));
      if (this._status.textContent !== status) this._status.textContent = status;
    }

    _scrollTo(left, behavior = this._motion?.matches ? 'auto' : 'smooth') {
      const target = this._clamp(left);
      this._requestedLeft = target;
      this._track.scrollTo({ left: target, behavior });
      this._queueUpdate();
    }

    _move(direction) {
      this._measure();
      const current = this._requestedLeft ?? this._left;
      const positions = direction > 0 ? this._positions : this._positions.slice().reverse();
      const destination = positions.find((position) => direction > 0 ? position.left > current + 1 : position.left < current - 1);
      this._scrollTo(destination ? destination.left : direction > 0 ? this._maximum : 0);
    }

    _onKeyDown(event) {
      if (event.target !== this._track || event.altKey || event.ctrlKey || event.metaKey || event.isComposing) return;
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      this._suppressClick = false;
      if (event.key === 'ArrowLeft') this._move(-1);
      else if (event.key === 'ArrowRight') this._move(1);
      else {
        this._measure();
        this._scrollTo(event.key === 'Home' ? 0 : this._maximum);
      }
    }

    _onPointerDown(event) {
      this._suppressClick = false;
      this._requestedLeft = null;
      if (event.pointerType !== 'mouse' || event.button !== 0 || event.isPrimary === false) return;
      if (event.target.closest?.('a, button, input, textarea, select, summary, [contenteditable="true"]')) return;
      this._measure();
      if (this._maximum <= 1) return;
      this._finishPointer(true);
      this._pointer = { id: event.pointerId, x: event.clientX, y: event.clientY, left: this._track.scrollLeft, dragging: false };
    }

    _onPointerMove(event) {
      const pointer = this._pointer;
      if (!pointer || event.pointerId !== pointer.id) return;
      const deltaX = event.clientX - pointer.x;
      const deltaY = event.clientY - pointer.y;
      if (!pointer.dragging) {
        if (Math.abs(deltaY) > 6 && Math.abs(deltaY) >= Math.abs(deltaX)) {
          this._finishPointer(true);
          return;
        }
        if (Math.abs(deltaX) <= 6 || Math.abs(deltaX) <= Math.abs(deltaY)) return;
        pointer.dragging = true;
        pointer.styles = {
          scrollBehavior: this._track.style.scrollBehavior,
          scrollSnapType: this._track.style.scrollSnapType,
          userSelect: this._track.style.userSelect,
          webkitUserSelect: this._track.style.webkitUserSelect
        };
        this._track.style.scrollBehavior = 'auto';
        this._track.style.scrollSnapType = 'none';
        this._track.style.userSelect = 'none';
        this._track.style.webkitUserSelect = 'none';
        this._track.classList.add('is-dragging');
        this._track.scrollTo({ left: this._track.scrollLeft, behavior: 'auto' });
        try { this._track.setPointerCapture(event.pointerId); } catch {}
        const selection = this.ownerDocument.getSelection?.();
        if (selection?.anchorNode && this._track.contains(selection.anchorNode)) selection.removeAllRanges();
      }
      event.preventDefault();
      this._measure();
      this._track.scrollLeft = this._clamp(pointer.left - deltaX);
      this._queueUpdate();
    }

    _onPointerEnd(event, cancelled = false) {
      if (this._pointer?.id === event.pointerId) this._finishPointer(cancelled);
    }

    _finishPointer(cancelled) {
      const pointer = this._pointer;
      if (!pointer) return;
      this._pointer = null;
      if (!pointer.dragging) return;
      this._suppressClick = !cancelled;
      this._track.classList.remove('is-dragging');
      Object.assign(this._track.style, pointer.styles);
      try {
        if (this._track.hasPointerCapture(pointer.id)) this._track.releasePointerCapture(pointer.id);
      } catch {}
      this._queueUpdate();
    }

    _onBlockSelect(event) {
      this._measure();
      const selected = this._cards.findIndex((card) => card.dataset.blockId === event.detail?.blockId || (event.target !== this && card.contains(event.target)));
      if (selected < 0) return;
      const position = this._positions[selected];
      if (position.left >= this._left && position.right <= this._left + this._width) return;
      this._scrollTo(position.left, 'auto');
    }
  }

  customElements.define('er-review-carousel', ErReviewCarousel);
})();
